import { Container, Title, Text, Button, Stack, Group, Card, SimpleGrid, ThemeIcon, Box, Badge } from '@mantine/core';
import { IconChartDots, IconChartBarPopular, IconFileUpload, IconClock, IconShieldCheck, IconArrowRight } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { Layout } from '../shared/components/Layout';
import { NewsGrid } from '../features/news';
import { QuickCalculator } from '../features/quick-calculator';
import { useOSD } from '../contexts/OSDContext';
import { getOSDMetadata, getDefaultOSD } from '../utils/osdMetadata';
import { useSEO } from '../shared/hooks';

export function Landing() {
  const { currentOSD, getOSDPath } = useOSD();

  // Get OSD metadata for dynamic content
  const osd = currentOSD || getDefaultOSD();
  const osdMetadata = getOSDMetadata(osd);
  const elicznikPortal = osdMetadata.elicznikPortal;

  useSEO({
    title: `Porównaj taryfy prądu ${osdMetadata.shortName} - Kalkulator 2026`,
    description: `Bezpłatne porównanie taryf energetycznych ${osdMetadata.shortName}. Sprawdź w 30 sekund czy przepłacasz za prąd.`,
  });

  return (
    <Layout isAuthenticated={false}>
      {/* Hero Section - QuickCalculator IS the hero */}
      <div style={{ backgroundColor: 'var(--mantine-color-ecoGreen-0)', padding: '2rem 0 3rem' }}>
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="xl">
            {/* Left side - Value proposition */}
            <Stack gap="lg" justify="center">
              <Badge
                size="lg"
                variant="light"
                color="ecoGreen"
                leftSection={<IconShieldCheck size={14} />}
                w="fit-content"
              >
                Darmowe • Bez rejestracji • {osdMetadata.shortName}
              </Badge>

              <Title order={1} size={40} fw={800} lh={1.2}>
                Sprawdź w{' '}
                <span style={{ color: 'var(--mantine-color-ecoGreen-6)' }}>
                  30 sekund
                </span>
                {' '}czy przepłacasz za prąd
              </Title>

              <Text size="lg" c="dimmed">
                Odpowiedz na 3 pytania i zobacz, która taryfa jest dla Ciebie najlepsza.
                Średnia oszczędność: <strong>340 zł rocznie</strong>.
              </Text>

              {/* Trust indicators - vertical for mobile */}
              <Stack gap="xs">
                <Group gap="xs">
                  <IconClock size={18} color="var(--mantine-color-ecoGreen-6)" />
                  <Text size="sm" c="dimmed">Wynik w 30 sekund</Text>
                </Group>
                <Group gap="xs">
                  <IconShieldCheck size={18} color="var(--mantine-color-ecoGreen-6)" />
                  <Text size="sm" c="dimmed">100% darmowe, bez rejestracji</Text>
                </Group>
                <Group gap="xs">
                  <IconChartBarPopular size={18} color="var(--mantine-color-ecoGreen-6)" />
                  <Text size="sm" c="dimmed">{osdMetadata.availableTariffs.length} taryf {osdMetadata.shortName}</Text>
                </Group>
              </Stack>
            </Stack>

            {/* Right side - QuickCalculator as hero */}
            <Box>
              <QuickCalculator />
            </Box>
          </SimpleGrid>
        </Container>
      </div>

      {/* Features Section - Simplified */}
      <Container size="lg" py={80}>
        <Title order={2} ta="center" mb="md">
          Dlaczego warto porównać taryfy?
        </Title>
        <Text ta="center" c="dimmed" mb={50} maw={600} mx="auto">
          Większość Polaków przepłaca za prąd, bo nie wie, że może zmienić taryfę.
          Sprawdź, która taryfa pasuje do Twojego stylu życia.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <ThemeIcon size={56} radius="md" color="ecoGreen" mb="md">
              <IconChartDots size={32} />
            </ThemeIcon>
            <Title order={3} mb="md">
              Dopasuj do swojego zużycia
            </Title>
            <Text c="dimmed">
              Kalkulator analizuje kiedy zużywasz prąd i dobiera taryfę do Twojego
              profilu - czy to pompa ciepła, fotowoltaika, czy samochód elektryczny.
            </Text>
          </Card>

          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <ThemeIcon size={56} radius="md" color="ecoGreen" mb="md">
              <IconChartBarPopular size={32} />
            </ThemeIcon>
            <Title order={3} mb="md">
              Porównaj wszystkie opcje
            </Title>
            <Text c="dimmed">
              Zobacz koszty taryf (G11, G12, G12w, G12as, G13) u 4 operatorów OSD
              na przejrzystych wykresach. Podział na energię i dystrybucję.
            </Text>
          </Card>

          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <ThemeIcon size={56} radius="md" color="ecoGreen" mb="md">
              <IconFileUpload size={32} />
            </ThemeIcon>
            <Title order={3} mb="md">
              Importuj dane z licznika
            </Title>
            <Text c="dimmed">
              Masz inteligentny licznik? Zaimportuj dane z {elicznikPortal.name} ({osdMetadata.shortName})
              i uzyskaj precyzyjną analizę na podstawie rzeczywistego zużycia.
            </Text>
          </Card>
        </SimpleGrid>
      </Container>

      {/* News Section - Hero + Grid layout */}
      <Box py={80} style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
        <Container size="lg">
          <NewsGrid maxItems={4} showViewAll={true} title="Aktualności" />
        </Container>
      </Box>

      {/* CTA Section */}
      <div style={{ backgroundColor: 'var(--mantine-color-ecoGreen-6)', color: 'white', padding: '4rem 0' }}>
        <Container size="md">
          <Stack gap="xl" align="center" ta="center">
            <Title order={2} c="white">
              Nie przepłacaj za prąd
            </Title>
            <Text size="lg" c="white" maw={500}>
              Zmiana taryfy jest darmowa i zajmuje kilka minut.
              Sprawdź, ile możesz zaoszczędzić.
            </Text>
            <Button
              size="xl"
              variant="white"
              color="ecoGreen"
              component={Link}
              to={getOSDPath('/kalkulatory/podstawowy')}
              rightSection={<IconArrowRight size={20} />}
            >
              Porównaj taryfy teraz
            </Button>
          </Stack>
        </Container>
      </div>
    </Layout>
  );
}
